import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { BaseActivityMessage } from '../BaseActivityMessage';
import { TextField } from '../TextField';

export interface NumberActivityMessageProps {
  /**
   * Current number value
   */
  value: string;
  /**
   * Callback when form is submitted
   */
  onSubmit: (value: string) => void;
  /**
   * Whether to show skip button
   */
  allowSkip?: boolean;
  /**
   * Skip button text
   */
  skipText?: string;
  /**
   * Submit button text
   */
  submitText?: string;
  /**
   * Callback when skip button is clicked
   */
  onSkip?: () => void;
  /**
   * Whether the inputs are disabled
   */
  disabled?: boolean;
  /**
   * Label/aria-label for the number input
   */
  label?: string;
  /**
   * Maximum value for number input
   */
  max?: number;
  /**
   * Minimum value for number input
   */
  min?: number;
  /**
   * Hide the submit button (used for locked / read-only rendering).
   */
  hideSubmit?: boolean;
  /**
   * Message content to display above the number input
   */
  children?: React.ReactNode;
  /**
   * Sender type: 'bot' or 'user'
   */
  sender: 'bot' | 'user';
  /**
   * Avatar URL for the sender
   */
  avatarUrl?: string;
  /**
   * Timestamp for the message
   */
  timestamp?: string;
  /**
   * Custom bot display name
   */
  botName?: string;
  /**
   * Optional content rendered below the prompt bubble (e.g. an AI-translation disclosure hint).
   */
  footer?: React.ReactNode;
}

/**
 * NumberActivityMessage displays a number input interface with submit and optional skip buttons.
 * This component is designed for collecting numeric values with min/max constraints in chat-like interfaces.
 */
export const NumberActivityMessage: React.FC<NumberActivityMessageProps> = ({
  value,
  onSubmit,
  allowSkip = false,
  skipText = 'Skip',
  submitText = 'Send',
  onSkip,
  disabled = false,
  label,
  max,
  min,
  hideSubmit = false,
  children,
  sender,
  avatarUrl,
  timestamp,
  botName,
  footer,
}) => {
  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      numberPrompt: value,
    },
  });

  const handleFormSubmit = ({ numberPrompt }: { numberPrompt: string }) => {
    onSubmit(numberPrompt);
  };

  const numberValue = watch('numberPrompt');

  const isSubmitDisabled =
    numberValue === undefined ||
    Number(numberValue) < (min ?? -Infinity) ||
    Number(numberValue) > (max ?? Infinity) ||
    disabled;

  return (
    <BaseActivityMessage
      allowSkip={allowSkip}
      skipText={skipText}
      submitText={submitText}
      onSkip={onSkip}
      disabled={disabled}
      submitEnabled={!isSubmitDisabled}
      hideSubmit={hideSubmit}
      sender={sender}
      avatarUrl={avatarUrl}
      timestamp={timestamp}
      botName={botName}
      footer={footer}
      onSubmit={() => {}}
      useForm={true}
      onFormSubmit={handleSubmit(handleFormSubmit)}
      renderInput={() => (
        <Controller
          name="numberPrompt"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              inputProps={{
                inputMode: 'numeric',
                min,
                max,
              }}
              sx={{ minWidth: '200px' }}
              type="number"
              slotProps={{
                htmlInput: {
                  'aria-label': label,
                },
              }}
              disabled={disabled}
            />
          )}
        />
      )}
    >
      {children}
    </BaseActivityMessage>
  );
};
